import React, { useState, useEffect, useMemo } from 'react';
import styles from './CommunityPage.module.css';
import API_BASE_URL from '../config/api';
import { Helmet } from '../utils/helmet';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import PostsSection from '../components/PostsSection';
import CommunityPageSkeleton from '../components/CommunityPageSkeleton';
import { useTranslation } from 'react-i18next';

function CommunityPage({ allMountains = [] }) {
  const [highestPoints, setHighestPoints] = useState([]);
  const [mostSummited, setMostSummited] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gearOptions, setGearOptions] = useState([]);
  const navigate = useNavigate();
  const { currentUser, firebaseUser } = useUser();
  const { t } = useTranslation();

  const helmet = useMemo(
    () => (
      <Helmet>
        <title>{t('community.helmetTitle')}</title>
        <meta
          name="description"
          content={t('community.helmetDescription')}
        />
      </Helmet>
    ),
    [t]
  );

  useEffect(() => {
    const fetchRanks = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch Highest Points Rank
        const highestPointsResponse = await fetch(`${API_BASE_URL}/api/ranks/highest-point`);
        const highestPointsData = await highestPointsResponse.json();
        if (highestPointsData.success) {
          setHighestPoints(highestPointsData.data);
        } else {
          console.error('Error fetching highest points rank:', highestPointsData.message);
          setError(prev => `${prev}${t('community.errors.highest')} `);
        }

        // Fetch Most Summited Rank
        const mostSummitedResponse = await fetch(`${API_BASE_URL}/api/ranks/summited-count`);
        const mostSummitedData = await mostSummitedResponse.json();
        if (mostSummitedData.success) {
          setMostSummited(mostSummitedData.data);
        } else {
          console.error('Error fetching most summited rank:', mostSummitedData.message);
          setError(prev => `${prev}${t('community.errors.summited')} `);
        }

      } catch (err) {
        console.error('Network error fetching ranks:', err);
        setError(t('community.errors.network'));
      } finally {
        setLoading(false);
      }
    };

    fetchRanks();
  }, []);

  useEffect(() => {
    const fetchGearCatalog = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/gear`);
        const payload = await response.json();
        if (payload.success) {
          setGearOptions(payload.data || []);
        }
      } catch (err) {
        console.warn('Unable to load gear catalog for tagging:', err);
      }
    };

    fetchGearCatalog();
  }, []);

  const getMedalEmoji = (rankPosition) => {
    if (rankPosition === 1) return '🥇';
    if (rankPosition === 2) return '🥈';
    if (rankPosition === 3) return '🥉';
    return `${rankPosition}.`;
  };

  const getRankItemKey = (user, index) =>
    user?._id || (user?.username ? `${user.username}-${index}` : `rank-${index}`);

  const handleUsernameClick = (username) => {
    // Navigate to the public profile page using react-router-dom
    console.log(`[CommunityPage] Clicked on username: ${username}`);
    navigate(`/profile/${username}`); // Use navigate for routing
  };

  if (loading) {
    return (
      <>
        {helmet}
        <CommunityPageSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        {helmet}
        <div className={styles.communityPage}>
          <p className={styles.error}>{error}</p>
        </div>
      </>
    );
  }

  // -------------------------------------------- RETURN STATEMENT ------------------------------------------------
  return (
    <>
      {helmet}
      <div className={styles.communityPage}>
      <h1 className={styles.title}>{t('community.title')}</h1>

      <div className={styles.ranks}>
      {/* ---------------- Highest Point Achieved Section ------------------ */}
      <section className={styles.rankSection}>
        <h2><svg style={{height: '2.2rem', width: '2.2rem', marginBottom: '-0.5rem'}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
          <path fillRule="evenodd" d="M11.47 10.72a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 1 1-1.06 1.06L12 12.31l-6.97 6.97a.75.75 0 0 1-1.06-1.06l7.5-7.5Z" clipRule="evenodd" />
          <path fillRule="evenodd" d="M11.47 4.72a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 1 1-1.06 1.06L12 6.31l-6.97 6.97a.75.75 0 0 1-1.06-1.06l7.5-7.5Z" clipRule="evenodd" />
        </svg> {t('community.highestPoint')}</h2>
        {highestPoints.filter(u => u.highestPoint > 0).length > 0 ? (
          <ol className={styles.rankList}>
            {highestPoints.filter(u => u.highestPoint > 0).slice(0, 5).map((user, index) => (
              <li key={getRankItemKey(user, index)} className={styles.rankItem}>
                <span className={styles.rankPosition}>{getMedalEmoji(index + 1)}</span>
                <span className={styles.nickname}>{user.nickname}</span>
                <span
                  className={styles.usernameLink}
                  onClick={() => handleUsernameClick(user.username)} // Pass user.username
                >
                  @{user.username}
                </span>
                <span className={styles.value}>{user.highestPoint}m</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className={styles.emptyState}>{t('community.noHighest')}</p>
        )}
      </section>

      {/* Most Mountains Summited Section */}
      <section className={styles.rankSection}>
          <h2><svg style={{ width: '2rem', height: '2rem', marginBottom: '-0.5rem' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
          <path d="M11.644 1.59a.75.75 0 0 1 .712 0l9.75 5.25a.75.75 0 0 1 0 1.32l-9.75 5.25a.75.75 0 0 1-.712 0l-9.75-5.25a.75.75 0 0 1 0-1.32l9.75-5.25Z" />
          <path d="m3.265 10.602 7.668 4.129a2.25 2.25 0 0 0 2.134 0l7.668-4.13 1.37.739a.75.75 0 0 1 0 1.32l-9.75 5.25a.75.75 0 0 1-.71 0l-9.75-5.25a.75.75 0 0 1 0-1.32l1.37-.738Z" />
          <path d="m10.933 19.231-7.668-4.13-1.37.739a.75.75 0 0 0 0 1.32l9.75 5.25c.221.12.489.12.71 0l9.75-5.25a.75.75 0 0 0 0-1.32l-1.37-.738-7.668 4.13a2.25 2.25 0 0 1-2.134-.001Z" />
        </svg> {t('community.mostSummited')}</h2>
        {mostSummited.filter(u => u.summitedCount > 0).length > 0 ? (
          <ol className={styles.rankList}>
            {mostSummited.filter(u => u.summitedCount > 0).slice(0, 5).map((user, index) => (
              <li key={getRankItemKey(user, index)} className={styles.rankItem}>
                <span className={styles.rankPosition}>{getMedalEmoji(index + 1)}</span>
                <span className={styles.nickname}>{user.nickname}</span>
                <span
                  className={styles.usernameLink}
                  onClick={() => handleUsernameClick(user.username)} // Pass user.username
                >
                  @{user.username}
                </span>
                <span className={styles.value}>{t('community.summitedCount', { count: user.summitedCount })}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className={styles.emptyState}>{t('community.noSummited')}</p>
        )}
      </section>
      </div>
      <PostsSection
        currentUser={currentUser}
        firebaseUser={firebaseUser}
        mountainOptions={allMountains}
        gearOptions={gearOptions}
      />
    </div>
    </>
  );
}

export default CommunityPage;
